import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CheckIcon from 'src/assets/icons/CheckIcon';
import ContractedIcon from 'src/assets/icons/ContractedIcon';
import CrossIcon from 'src/assets/icons/CrossIcon';
import DownloadIcon from 'src/assets/icons/DownloadIcon';
import Button from 'src/components/design-system/Button';
import useRedirect from 'src/hooks/useRedirect';
import useGetExternalCatalogMutation from 'src/services/catalog_products/useGetExternalCatalogMutation';
import type { RecommendationItem, RecommendPageParams } from 'src/types/recommendation_items';
import {
  buildPunchoutSupplierExportRows,
  exportRecommendationsExcel,
  PUNCHOUT_SUPPLIER_COLUMN_MAPPING,
  PUNCHOUT_SUPPLIER_COLUMNS,
  RECOMMENDATION_DISCLAIMER,
  sanitizeSupplierFileSegment,
} from 'src/utils/recommendationExcelExport';

const PAGE_TYPE_PO = 0;

export type StagedActionType = 'swap' | 'addToCart' | 'punchout';

export interface StagedAction {
  action: StagedActionType;
  item: RecommendationItem;
}

export interface BlockDisplayInfo {
  description: string;
  price: string;
  uom: string;
}

export type SaveProgressStatus = 'pending' | 'saving' | 'success' | 'error';

export interface SaveRecommendationItemsProps {
  stagedActions: Record<number, StagedAction>;
  saveProgress: Record<number, SaveProgressStatus>;
  blockDisplayList: BlockDisplayInfo[];
  blockContextList: (RecommendPageParams | null)[];
  pageType?: number;
}

type Entry = {
  blockIndex: number;
  staged: StagedAction;
  blockDisplay: BlockDisplayInfo | undefined;
  lineItemId?: number;
};

const getSaveActionLabel = (action: StagedActionType, resolved: boolean): string => {
  if (action === 'swap') return resolved ? 'Item Swapped' : 'Swapping Item';
  if (action === 'addToCart') return resolved ? 'Added to Cart' : 'Adding to Cart';
  return resolved ? 'Punchout completed' : 'Punchout';
};

const SaveCard = ({
  lineItemId,
  blockDisplay,
  staged,
  progress,
}: {
  lineItemId?: number;
  blockDisplay: BlockDisplayInfo | undefined;
  staged: StagedAction;
  progress: 'pending' | 'saving' | 'success' | 'error';
}) => {
  const isResolved = progress === 'success';
  const isError = progress === 'error';
  const isLoading = progress === 'pending' || progress === 'saving';
  const statusLabel = getSaveActionLabel(staged.action, isResolved);
  const { t } = useTranslation();

  return (
    <div className="border border-[#E5E7EB] rounded-lg p-4 bg-white shadow-sm space-y-2 min-w-0">
      {/* Item badge + primary description */}
      <div className="flex items-start gap-3 min-w-0">
        {lineItemId != null && (
          <span className="flex-shrink-0 rounded-full bg-[#3FA9DB33] px-2.5 py-1 text-xs font-medium text-black leading-normal">
            Item {lineItemId}
          </span>
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <h3 className="font-medium text-[13px] text-[#000000] leading-tight truncate">
            {blockDisplay?.description ?? ''}
          </h3>
        </div>
      </div>
      {/* Inner card: recommended product + item action on the right */}
      <div
        className={`border border-[#D1D1D1] rounded-lg bg-white p-2 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4 min-w-0 overflow-hidden${
          lineItemId != null ? ' ml-6' : ''
        }`}
      >
        <div className="min-w-0 w-full overflow-hidden space-y-1 lg:flex-1">
          <div className="font-semibold text-[13px] text-gray-900 truncate">
            <span
              {...(staged.item.description
                ? {
                    'data-tooltip-id': 'recommended-panel-tooltip',
                    'data-tooltip-content': staged.item.description,
                  }
                : {})}
            >
              {staged.item.description}
            </span>
          </div>
          <p className="text-[13px] text-[#4A5565] flex gap-1 min-w-0">
            <span className="flex-shrink-0">{t('Distributor')}:</span>
            <span className="min-w-0 truncate text-[#111111]">{staged.item.supplierName}</span>
          </p>
        </div>
        <div className="flex w-full shrink-0 items-center justify-start gap-2 lg:w-auto lg:justify-end">
          {isLoading && (
            <>
              <span className="k-icon k-i-loading k-icon-sm text-gray-400 animate-spin" />
              <span className="text-[13px] font-medium text-[#000000]">{statusLabel}</span>
            </>
          )}
          {isResolved && (
            <>
              <CheckIcon color="#00B321" width="18" height="18" />
              <span className="text-[13px] font-medium text-[#000000]">{statusLabel}</span>
            </>
          )}
          {isError && (
            <>
              <CrossIcon color="#d9534f" width="18" height="18" />
              <span className="text-[13px] font-medium text-red-600">{t('Failed')}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function PunchoutCard({
  lineItemId,
  blockDisplay,
  staged,
}: {
  lineItemId?: number;
  blockDisplay: BlockDisplayInfo | undefined;
  staged: StagedAction;
}) {
  const priceStr =
    blockDisplay?.price != null && blockDisplay.price !== ''
      ? `$${Number(blockDisplay.price).toFixed(2)}`
      : staged.item?.price != null
      ? `$${Number(staged.item.price).toFixed(2)}`
      : '';
  const uom = blockDisplay?.uom || staged.item.uom || '';
  const recommendedPriceStr =
    staged.item?.price != null ? `$${Number(staged.item.price).toFixed(2)}` : '';
  const { t } = useTranslation();

  return (
    <div className="p-4 space-y-2 min-w-0">
      {/* Item badge + primary description + price (same row/layout as RecommendationItems) */}
      <div className="flex items-start gap-3 min-w-0">
        {lineItemId != null && (
          <span className="flex-shrink-0 rounded-full bg-[#3FA9DB33] px-2.5 py-1 text-xs font-medium text-black leading-normal">
            Item {lineItemId}
          </span>
        )}
        <div className="min-w-0 flex-1 overflow-hidden">
          <h3 className="font-medium text-[13px] text-[#000000] leading-tight truncate">
            {blockDisplay?.description ?? staged.item.description}
          </h3>
          {(priceStr || uom) && (
            <p className="mt-1 text-[13px] text-[#000000]">
              <span className="font-semibold">{priceStr}</span>
              {uom ? ` / ${uom}` : ''}
            </p>
          )}
        </div>
      </div>
      {/* Inner card: recommended product (match RecommendationItems card layout) */}
      <div
        className={`border border-[#D1D1D1] rounded-lg bg-white p-2 space-y-2 min-w-0 overflow-hidden${
          lineItemId != null ? ' ml-6' : ''
        }`}
      >
        <div className="min-w-0 overflow-hidden">
          <div className="flex items-center min-w-0 gap-2">
            <span
              className="min-w-0 flex-1 truncate font-semibold text-[13px] text-gray-900"
              {...(staged.item.description
                ? {
                    'data-tooltip-id': 'recommended-panel-tooltip',
                    'data-tooltip-content': staged.item.description,
                  }
                : {})}
            >
              {staged.item.description}
            </span>
            {staged.item.isContracted && (
              <span
                className="flex-shrink-0"
                data-tooltip-id="recommended-panel-tooltip"
                data-tooltip-content={t('Contracted Item')}
              >
                <ContractedIcon />
              </span>
            )}
          </div>
          {recommendedPriceStr && (
            <div className="text-[13px]">
              <span className="font-semibold text-gray-900">{recommendedPriceStr}</span>
              <span className="text-gray-900">{uom ? ` / ${uom}` : ''}</span>
            </div>
          )}
        </div>
        <div className="space-y-0.5 text-[13px]">
          <div className="flex min-w-0 gap-1">
            <span className="flex-shrink-0 text-[#4A5565]">{t('Item#')}:</span>
            <span className="min-w-0 truncate text-[#111111]">{staged.item.itemNumber}</span>
          </div>
          <div className="flex min-w-0 gap-1">
            <span className="flex-shrink-0 text-[#4A5565]">{t('Distributor')}:</span>
            <span className="min-w-0 truncate text-[#111111]">{staged.item.supplierName}</span>
          </div>
          <div className="flex min-w-0 gap-1">
            <span className="flex-shrink-0 text-[#4A5565]">{t('Manufacturer')}:</span>
            <span className="min-w-0 truncate text-[#111111]">{staged.item.manufacturerName}</span>
          </div>
          <div className="flex min-w-0 gap-1">
            <span className="flex-shrink-0 text-[#4A5565]">{t('Mfg#')}:</span>
            <span className="min-w-0 truncate text-[#111111]">
              {staged.item.manufacturerNumber}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const SaveRecommendationItems = ({
  stagedActions,
  saveProgress,
  blockDisplayList,
  blockContextList,
  pageType,
}: SaveRecommendationItemsProps) => {
  const { mutateAsync: fetchExternalCatalog, isPending: isFetchingExternalCatalog } =
    useGetExternalCatalogMutation();
  const { redirect } = useRedirect();
  const { t } = useTranslation();

  const { swapOnPo, swapOnCart, addToCart, punchoutByCatalogId } = useMemo(() => {
    const entries = (Object.entries(stagedActions) as [string, StagedAction][])
      .filter(([blockIndexStr]) => saveProgress[Number(blockIndexStr)] != null)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([blockIndexStr, staged]): Entry => {
        const blockIndex = Number(blockIndexStr);
        const ctx = blockContextList[blockIndex] ?? null;
        return {
          blockIndex,
          staged,
          blockDisplay: blockDisplayList[blockIndex],
          lineItemId: ctx?.lineItemId,
        };
      });

    const swapOnPo: Entry[] = [];
    const swapOnCart: Entry[] = [];
    const addToCart: Entry[] = [];
    const punchoutEntries: Entry[] = [];

    for (const e of entries) {
      if (e.staged.action === 'swap') {
        if (pageType === PAGE_TYPE_PO) swapOnPo.push(e);
        else swapOnCart.push(e);
      } else if (e.staged.action === 'addToCart') {
        addToCart.push(e);
      } else if (e.staged.action === 'punchout') {
        punchoutEntries.push(e);
      }
    }

    const punchoutByCatalogId = new Map<number, Entry[]>();
    for (const e of punchoutEntries) {
      const { catalogId } = e.staged.item;
      const list = punchoutByCatalogId.get(catalogId) ?? [];
      list.push(e);
      punchoutByCatalogId.set(catalogId, list);
    }

    return { swapOnPo, swapOnCart, addToCart, punchoutByCatalogId };
  }, [stagedActions, saveProgress, blockDisplayList, blockContextList, pageType]);

  const sectionTitleClass = 'text-[13px] font-medium text-black mb-2 uppercase';

  const handlePunchoutRedirect = useCallback(
    async (catalogId: number) => {
      const data = await fetchExternalCatalog(catalogId);
      if (data.statusCode === 200 && data.redirectUrl) {
        redirect(data.redirectUrl, true, true);
      } else {
        toast.error(
          t('Unable to open the external catalog. Please connect with the administrator.')
        );
      }
    },
    [fetchExternalCatalog, redirect, t]
  );

  const handlePunchoutSupplierDownload = useCallback(
    (items: Entry[], supplierName: string) => {
      if (!items.length) return;
      const exportEntries = items.map((e) => ({
        staged: e.staged,
        blockDisplay: e.blockDisplay,
        blockContext: blockContextList[e.blockIndex] ?? null,
      }));
      const rows = buildPunchoutSupplierExportRows(exportEntries);
      const safeSupplier = sanitizeSupplierFileSegment(supplierName);
      const prefix = pageType === PAGE_TYPE_PO ? 'PO' : 'Cart';
      exportRecommendationsExcel({
        columns: [...PUNCHOUT_SUPPLIER_COLUMNS],
        rows,
        columnMapping: PUNCHOUT_SUPPLIER_COLUMN_MAPPING,
        fileName: `${prefix}_Punchout_Order_${safeSupplier}`,
        sheetName: supplierName.slice(0, 31),
        message: RECOMMENDATION_DISCLAIMER,
        blankRowAfterMessage: true,
        mergeLeadingColumnCount: 4,
        groupRowCounts: items.map(() => 1),
      });
    },
    [blockContextList, pageType]
  );

  return (
    <div className="overflow-y-auto space-y-6">
      {swapOnPo.length > 0 && (
        <section>
          <h2 className={sectionTitleClass}>{t('On the Current PO')}</h2>
          <div className="space-y-2">
            {swapOnPo.map((e) => (
              <SaveCard
                key={`save-swap-${e.blockIndex}`}
                lineItemId={e.lineItemId}
                blockDisplay={e.blockDisplay}
                staged={e.staged}
                progress={saveProgress[e.blockIndex] ?? 'pending'}
              />
            ))}
          </div>
        </section>
      )}

      {swapOnCart.length > 0 && (
        <section>
          <h2 className={sectionTitleClass}>{t('Swapped in Cart')}</h2>
          <div className="space-y-2">
            {swapOnCart.map((e) => (
              <SaveCard
                key={`save-swap-cart-${e.blockIndex}`}
                lineItemId={e.lineItemId}
                blockDisplay={e.blockDisplay}
                staged={e.staged}
                progress={saveProgress[e.blockIndex] ?? 'pending'}
              />
            ))}
          </div>
        </section>
      )}

      {addToCart.length > 0 && (
        <section>
          <h2 className={sectionTitleClass}>{t('Add to Cart')}</h2>
          <div className="space-y-2">
            {addToCart.map((e) => (
              <SaveCard
                key={`save-add-${e.blockIndex}`}
                lineItemId={e.lineItemId}
                blockDisplay={e.blockDisplay}
                staged={e.staged}
                progress={saveProgress[e.blockIndex] ?? 'pending'}
              />
            ))}
          </div>
        </section>
      )}

      {punchoutByCatalogId.size > 0 && (
        <section>
          <div className="mb-2">
            <h2 className={sectionTitleClass}>{t('Punchout List')}</h2>
            <p className="text-[13px] text-[#4A5565]">{t('punchoutListDescription')}</p>
          </div>
          <div className="space-y-2">
            {punchoutByCatalogId.size > 0 && (
              <div className="space-y-2">
                {Array.from(punchoutByCatalogId.entries()).map(([catalogId, items]) => {
                  const supplierName = items[0]?.staged.item.supplierName ?? 'Supplier';
                  return (
                    <div
                      key={`punchout-catalog-${catalogId}`}
                      className="border border-[#E5E7EB] rounded-lg overflow-hidden shadow-sm"
                    >
                      <div className="flex items-center justify-between flex-shrink-0 bg-[rgba(0,142,220,0.1)] px-4 py-2 min-w-0 gap-4">
                        <span className="font-medium text-[13px] text-[#000000] truncate min-w-0 flex-1">
                          {supplierName}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            color="info"
                            size="md"
                            loading={isFetchingExternalCatalog}
                            onClick={() => handlePunchoutRedirect(catalogId)}
                          >
                            {t('Punchout to Order')}
                          </Button>
                          {pageType === PAGE_TYPE_PO && (
                            <Button
                              color="secondary"
                              size="md"
                              data-tooltip-id="recommended-panel-tooltip"
                              data-tooltip-content={`${t('Download')}`}
                              onClick={() => handlePunchoutSupplierDownload(items, supplierName)}
                            >
                              <DownloadIcon width="14" height="14" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="bg-white shadow-sm">
                        {items.map((e) => (
                          <PunchoutCard
                            key={`save-punchout-${e.blockIndex}`}
                            lineItemId={e.lineItemId}
                            blockDisplay={e.blockDisplay}
                            staged={e.staged}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default SaveRecommendationItems;
